// ===========================================================================
// CANONICAL — Questionnaire Q1–Q30 (Web + Mobile) — Keys, Types, Options, Rules
// ===========================================================================

export type QuestionType = 'single_select' | 'multi_select' | 'yes_no' | 'dropdown';

export interface QuestionOption {
  label: string;
  value: string;
  revealsTextInput?: boolean;
}

export interface Question {
  id: string;
  key: string;
  text: string;
  type: QuestionType;
  options: QuestionOption[];
  required?: boolean;
  conditionalOn?: {
    questionKey: string;
    value: string | string[];
    dynamicText?: {
      [value: string]: string;
    };
  };
  derivedKey?: string;
  freeTextKey?: string;
  section: string;
}

// Section A: Worker Identity & Employment (Q1–Q3)
const sectionA: Question[] = [
  {
    id: 'q1',
    key: 'q1_worker_status',
    text: 'Are you currently a gig worker or 1099 independent contractor that does work for hire with any major company within the U.S.?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Worker Identity',
  },
  {
    id: 'q2',
    key: 'q2_companies_selected',
    text: 'What company do you currently work with as a 1099 Independent Contractor, Gig Worker, or Driver?',
    type: 'multi_select',
    options: [
      { label: 'Uber', value: 'uber' },
      { label: 'Lyft', value: 'lyft' },
      { label: 'DoorDash', value: 'doordash' },
      { label: 'Instacart', value: 'instacart' },
      { label: 'Amazon Flex', value: 'amazon_flex' },
      { label: 'Grubhub', value: 'grubhub' },
      { label: 'Sparks', value: 'sparks' },
      { label: 'TaskRabbit', value: 'taskrabbit' },
      { label: 'Fiverr', value: 'fiverr' },
      { label: 'UZURV', value: 'uzurv' },
      { label: 'Turo', value: 'turo' },
      { label: 'Shipt', value: 'shipt' },
      { label: 'Company Not Listed', value: 'not_listed', revealsTextInput: true },
    ],
    required: true,
    freeTextKey: 'q2_company_not_listed_text',
    section: 'Worker Identity',
  },
  {
    id: 'q3',
    key: 'q3_work_types_selected',
    text: 'Select type of work you do (multi-select allowed).',
    type: 'multi_select',
    options: [
      { label: 'Rideshare', value: 'rideshare' },
      { label: 'Grocery Delivery', value: 'grocery_delivery' },
      { label: 'Food Delivery', value: 'food_delivery' },
      { label: 'Courier', value: 'courier' },
      { label: 'Cargo / Freight', value: 'cargo_freight' },
      { label: 'Truck Driver', value: 'truck_driver' },
      { label: 'Freelancer', value: 'freelancer' },
      { label: 'Package Delivery', value: 'package_delivery' },
      { label: 'Store Delivery', value: 'store_delivery' },
      { label: 'Independent Contractor', value: 'independent_contractor' },
      { label: 'Work Type Not Listed', value: 'not_listed', revealsTextInput: true },
    ],
    required: true,
    freeTextKey: 'q3_work_type_not_listed_text',
    section: 'Worker Identity',
  },
];

// Section B: Hours & Workload (Q4–Q8)
const sectionB: Question[] = [
  {
    id: 'q4',
    key: 'q4_hours_per_week_band',
    text: 'How many hours per week do you work?',
    type: 'dropdown',
    options: [
      { label: '0–10 hours', value: '0_10' },
      { label: '11–20 hours', value: '11_20' },
      { label: '21–30 hours', value: '21_30' },
      { label: '31–40 hours', value: '31_40' },
      { label: '41–60+ hours', value: '41_60_plus' },
    ],
    required: true,
    section: 'Hours & Workload',
  },
  {
    id: 'q5',
    key: 'q5_days_per_week',
    text: 'How many days a week do you work?',
    type: 'dropdown',
    options: [
      { label: '1 day', value: '1' },
      { label: '2 days', value: '2' },
      { label: '3 days', value: '3' },
      { label: '4 days', value: '4' },
      { label: '5 days', value: '5' },
      { label: '6 days', value: '6' },
      { label: '7 days', value: '7' },
    ],
    required: true,
    section: 'Hours & Workload',
  },
  {
    id: 'q6',
    key: 'q6_income_satisfied',
    text: 'Are you satisfied with your current wages/income?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Hours & Workload',
  },
  {
    id: 'q7',
    key: 'q7_enough_work',
    text: 'Do you feel like you\'re getting enough work?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Hours & Workload',
  },
  {
    id: 'q8',
    key: 'q8_overworked',
    text: 'Do you feel overworked?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Hours & Workload',
  },
];

// Section C: Health, Insurance & Benefits (Q9–Q24)
const sectionC: Question[] = [
  {
    id: 'q9',
    key: 'q9_received_stipend',
    text: 'Have you received any healthcare stipend from a gig company (Uber, Lyft, DoorDash, etc.)?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  {
    id: 'q10',
    key: 'q10_eligible_stipend',
    text: 'Are you eligible for any healthcare stipend from a gig company?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  // Q12 moved BEFORE Q11 - "Do you currently have health insurance?" comes before satisfaction question
  {
    id: 'q12',
    key: 'q12_has_health_insurance',
    text: 'Do you currently have health insurance?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  // Q11 now comes AFTER Q12 - skip if user has NO health insurance
  {
    id: 'q11',
    key: 'q11_followup',
    text: 'Are you satisfied with your healthcare payment?', // Default text, will be overridden
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    conditionalOn: {
      questionKey: 'q12_has_health_insurance',
      value: 'yes', // Only show if user HAS health insurance
      dynamicText: {
        yes: 'Are you satisfied with your healthcare payment?',
        no: 'Would you like more affordable healthcare?',
      },
    },
    derivedKey: 'q11_prompt_type', // satisfaction | affordability
    section: 'Health & Benefits',
  },
  {
    id: 'q13',
    key: 'q13_looking_for_health_insurance',
    text: 'Are you currently looking for health insurance?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  {
    id: 'q14',
    key: 'q14_dental_vision',
    text: 'Do you currently have dental and/or vision coverage?',
    type: 'single_select',
    options: [
      { label: 'I have dental', value: 'dental' },
      { label: 'I have vision', value: 'vision' },
      { label: 'I have both', value: 'both' },
      { label: 'No', value: 'none' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  {
    id: 'q15',
    key: 'q15_need_dental',
    text: 'Do you need dental insurance?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  {
    id: 'q16',
    key: 'q16_need_chiropractic',
    text: 'Do you need chiropractic therapy?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  {
    id: 'q17',
    key: 'q17_need_life_insurance',
    text: 'Do you currently need life insurance?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  {
    id: 'q18',
    key: 'q18_need_retirement_planning',
    text: 'Do you need retirement planning services?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  {
    id: 'q19',
    key: 'q19_need_financial_services',
    text: 'Do you need financial and investment services?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Health & Benefits',
  },
  // Skip if user has NO health insurance (q12 = no)
  {
    id: 'q20',
    key: 'q20_coverage_affordable',
    text: 'Do you think your current health coverage is affordable?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    conditionalOn: {
      questionKey: 'q12_has_health_insurance',
      value: 'yes', // Only show if user HAS health insurance
    },
    section: 'Health & Benefits',
  },
  // Skip if user has NO health insurance (q12 = no)
  {
    id: 'q21',
    key: 'q21_want_pay_less',
    text: 'Would you like to pay less for health insurance?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    conditionalOn: {
      questionKey: 'q12_has_health_insurance',
      value: 'yes', // Only show if user HAS health insurance
    },
    section: 'Health & Benefits',
  },
  // Skip if user has NO health insurance (q12 = no)
  {
    id: 'q22',
    key: 'q22_current_premium_band',
    text: 'How much do you currently pay for health insurance?',
    type: 'dropdown',
    options: [
      { label: '$0–$100', value: '0_100' },
      { label: '$100–$200', value: '100_200' },
      { label: '$200–$300', value: '200_300' },
      { label: '$300–$400', value: '300_400' },
      { label: '$400–$500', value: '400_500' },
      { label: '$500–$700', value: '500_700' },
      { label: '$700–$900', value: '700_900' },
      { label: '$900–$1100', value: '900_1100' },
      { label: '$1100–$1500', value: '1100_1500' },
      { label: '$1500–$2000', value: '1500_2000' },
      { label: '$2000+', value: '2000_plus' },
    ],
    required: true,
    conditionalOn: {
      questionKey: 'q12_has_health_insurance',
      value: 'yes', // Only show if user HAS health insurance
    },
    section: 'Health & Benefits',
  },
  {
    id: 'q23',
    key: 'q23_desired_premium_band',
    text: 'Based on your household size, how much would you like to pay for health insurance?',
    type: 'dropdown',
    options: [
      { label: '$0–$100', value: '0_100' },
      { label: '$100–$200', value: '100_200' },
      { label: '$200–$300', value: '200_300' },
      { label: '$300–$400', value: '300_400' },
      { label: '$400–$500', value: '400_500' },
      { label: '$500–$700', value: '500_700' },
      { label: '$700–$900', value: '700_900' },
      { label: '$900–$1100', value: '900_1100' },
      { label: '$1100–$1500', value: '1100_1500' },
      { label: '$1500+', value: '1500_plus' },
    ],
    required: true,
    // Skip if user answered "no" to BOTH q12 (has health insurance) AND q13 (looking for health insurance)
    conditionalOn: {
      questionKey: '__skip_insurance_questions__',
      value: 'show',
    },
    section: 'Health & Benefits',
  },
  {
    id: 'q24',
    key: 'q24_desired_income_pct_band',
    text: 'What percentage of your monthly income would you like to pay for healthcare?',
    type: 'dropdown',
    options: [
      { label: '1%', value: 'p01' },
      { label: '2%', value: 'p02' },
      { label: '3%', value: 'p03' },
      { label: '4%', value: 'p04' },
      { label: '5%', value: 'p05' },
      { label: '6–10%', value: 'p06_10' },
      { label: '11–15%', value: 'p11_15' },
      { label: '16–20%', value: 'p16_20' },
      { label: '20–25%', value: 'p20_25' },
    ],
    required: true,
    // Skip if user answered "no" to BOTH q12 (has health insurance) AND q13 (looking for health insurance)
    conditionalOn: {
      questionKey: '__skip_insurance_questions__',
      value: 'show',
    },
    section: 'Health & Benefits',
  },
];

// Section D: Wellbeing & Legal Needs (Q25–Q28)
const sectionD: Question[] = [
  {
    id: 'q25',
    key: 'q25_back_pain',
    text: 'Do you have back pain from driving or sitting too long?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Wellbeing & Legal',
  },
  {
    id: 'q26',
    key: 'q26_fatigue',
    text: 'Do you experience fatigue from overworking?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Wellbeing & Legal',
  },
  {
    id: 'q27',
    key: 'q27_want_pto',
    text: 'Would you like Paid Time Off (PTO) benefits?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Wellbeing & Legal',
  },
  {
    id: 'q28',
    key: 'q28_need_legal_help',
    text: 'Do you need any legal assistance with accidents, work-related issues, or other matters?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Wellbeing & Legal',
  },
];

// Section E: Community & Future Work (Q29–Q30)
const sectionE: Question[] = [
  {
    id: 'q29',
    key: 'q29_interest_other_industries',
    text: 'Are you interested in working as a 1099 independent contractor for another industry or gig-based company?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Community & Future',
  },
  {
    id: 'q30',
    key: 'q30_attend_local_meetups',
    text: 'Would you be willing to attend local meetups to privately discuss gig worker issues with others?',
    type: 'yes_no',
    options: [
      { label: 'Yes', value: 'yes' },
      { label: 'No', value: 'no' },
    ],
    required: true,
    section: 'Community & Future',
  },
];

// Export all questions in order
export const allQuestions: Question[] = [
  ...sectionA,
  ...sectionB,
  ...sectionC,
  ...sectionD,
  ...sectionE,
];

// Export sections for grouping
export const questionSections = {
  'Worker Identity': sectionA,
  'Hours & Workload': sectionB,
  'Health & Benefits': sectionC,
  'Wellbeing & Legal': sectionD,
  'Community & Future': sectionE,
};

export const sectionOrder = [
  'Worker Identity',
  'Hours & Workload',
  'Health & Benefits',
  'Wellbeing & Legal',
  'Community & Future',
];

// Get question by key
export function getQuestionByKey(key: string): Question | undefined {
  return allQuestions.find(q => q.key === key);
}

// Get question by id
export function getQuestionById(id: string): Question | undefined {
  return allQuestions.find(q => q.id === id);
}
